import React, { useEffect, useRef, useState } from 'react';
import ImageMapper from '../ImageMapper/ImageMapper';
import { Spacing } from '../Spacing/Spacing';
import { COLORS } from '../../constants/COLORS';
import { Add } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { isMobile } from '../../services/screen';

const ImagePlant = ({
  enterprise,
  plant,
  maxWidth,
  maxHeight,
  onClick,
  isView = false,
  showBackButton = isView,
  showFullscreen = isView && !isMobile,
  useFullViewportOnFullscreen = true,
  scrollable = true,
  background = COLORS.BLACK_TRANSPARENT,
  enableTouchpadNavigation = false,
  showZoomButtons = true,
  showHelpButton = false,
  onHelp,
  viewerControls = false,
}) => {
  const [maxHeightInternal, setMaxHeightInternal] = useState(0);
  const [maxWidthInternal, setMaxWidthInternal] = useState(0);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const touchGesture = useRef();
  const mouseGesture = useRef();
  const scrollRef = useRef();
  const navigate = useNavigate();

  function calcWidth() {
    return (maxHeightInternal * enterprise?.imageOrigen?.xScreen) / enterprise?.imageOrigen?.yScreen;
  }

  function calcHeight() {
    return (maxWidthInternal * enterprise?.imageOrigen?.yScreen) / enterprise?.imageOrigen?.xScreen;
  }

  const calcSize = () => {
    let tempWidth;
    let tempHeight;
    if (maxHeightInternal > maxWidthInternal) {
      tempWidth = calcWidth();
      tempHeight = maxHeightInternal;
      if (tempWidth > maxWidthInternal) {
        tempHeight = calcHeight();
        tempWidth = maxWidthInternal;
      }
    } else {
      tempHeight = calcHeight();
      tempWidth = maxWidthInternal;
      if (tempHeight > maxHeightInternal) {
        tempWidth = calcWidth();
        tempHeight = maxHeightInternal;
      }
    }
    setWidth(tempWidth);
    setHeight(tempHeight);
  };

  const getTouchpadDimensions = zoomValue => {
    const fitScale =
      enableTouchpadNavigation && width > 0 && height > 0
        ? Math.min(1, maxWidthInternal / width, maxHeightInternal / height)
        : 1;
    return {
      width: width * fitScale * zoomValue,
      height: height * fitScale * zoomValue,
    };
  };

  const clampPan = (position, zoomValue = zoom) => {
    const dimensions = getTouchpadDimensions(zoomValue);
    const maxPanX = Math.max(0, dimensions.width - maxWidthInternal);
    const maxPanY = Math.max(0, dimensions.height - maxHeightInternal);
    return {
      x: Math.min(0, Math.max(-maxPanX, position.x)),
      y: Math.min(0, Math.max(-maxPanY, position.y)),
    };
  };

  const onZoom = event => {
    if (enableTouchpadNavigation) {
      event.preventDefault();
      const isMouseWheel = Math.abs(event.deltaY) >= 40 && event.deltaX === 0;
      if (event.ctrlKey || event.metaKey || isMouseWheel) {
        const nextZoom = Math.min(4, Math.max(1, zoom - event.deltaY * 0.01));
        setZoom(nextZoom);
        setPan({ x: 0, y: 0 });
        return;
      }

      if (zoom > 1) {
        setPan(currentPan => clampPan({ x: currentPan.x - event.deltaX, y: currentPan.y - event.deltaY }));
      }
      return;
    }

    if (event.altKey) {
      if (event.deltaY < 0) {
        setZoom(zoom + 0.25);
      } else if (zoom > 1) {
        setZoom(zoom - 0.25);
      }
    }
  };

  const getTouchDistance = touches => {
    const first = touches[0];
    const second = touches[1];
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  };

  const onTouchStart = event => {
    if (!enableTouchpadNavigation) return;
    if (event.touches.length === 2) {
      touchGesture.current = {
        type: 'pinch',
        distance: getTouchDistance(event.touches),
        zoom,
      };
    } else if (event.touches.length === 1) {
      touchGesture.current = {
        type: 'pan',
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        pan,
      };
    }
  };

  const onTouchMove = event => {
    if (!enableTouchpadNavigation || !touchGesture.current) return;
    event.preventDefault();

    if (event.touches.length === 2 && touchGesture.current.type === 'pinch') {
      const distance = getTouchDistance(event.touches);
      const nextZoom = Math.min(4, Math.max(1, touchGesture.current.zoom * (distance / touchGesture.current.distance)));
      setZoom(nextZoom);
      setPan({ x: 0, y: 0 });
      return;
    }

    if (event.touches.length === 1 && touchGesture.current.type === 'pan' && zoom > 1) {
      const touch = event.touches[0];
      setPan(
        clampPan({
          x: touchGesture.current.pan.x + touch.clientX - touchGesture.current.x,
          y: touchGesture.current.pan.y + touch.clientY - touchGesture.current.y,
        })
      );
    }
  };

  const onTouchEnd = () => {
    touchGesture.current = undefined;
  };

  const onMouseDown = event => {
    if (!enableTouchpadNavigation || event.button !== 0) return;
    event.currentTarget.focus();
    mouseGesture.current = {
      x: event.clientX,
      y: event.clientY,
      pan,
      moved: false,
    };
  };

  const onMouseMove = event => {
    if (!enableTouchpadNavigation || !mouseGesture.current || zoom <= 1) return;
    const nextPan = {
      x: mouseGesture.current.pan.x + event.clientX - mouseGesture.current.x,
      y: mouseGesture.current.pan.y + event.clientY - mouseGesture.current.y,
    };
    mouseGesture.current.moved = true;
    event.preventDefault();
    setPan(clampPan(nextPan));
  };

  const onMouseEnd = () => {
    mouseGesture.current = undefined;
  };

  const onKeyDown = event => {
    if (!enableTouchpadNavigation) return;
    const panStep = Math.max(24, Math.round(Math.min(maxWidthInternal, maxHeightInternal) * 0.08));
    let nextPan;
    if (event.key === 'ArrowLeft') nextPan = { x: pan.x + panStep, y: pan.y };
    if (event.key === 'ArrowRight') nextPan = { x: pan.x - panStep, y: pan.y };
    if (event.key === 'ArrowUp') nextPan = { x: pan.x, y: pan.y + panStep };
    if (event.key === 'ArrowDown') nextPan = { x: pan.x, y: pan.y - panStep };
    if (nextPan) {
      event.preventDefault();
      setPan(clampPan(nextPan));
      return;
    }
    if (event.key === '+' || event.key === '=' || event.key === 'Add') {
      event.preventDefault();
      setZoom(currentZoom => Math.min(4, currentZoom + 0.25));
    }
    if (event.key === '-' || event.key === '_' || event.key === 'Subtract') {
      event.preventDefault();
      setZoom(currentZoom => Math.max(1, currentZoom - 0.25));
    }
    if (event.key === '0') {
      event.preventDefault();
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  };

  useEffect(() => {
    setMaxHeightInternal(maxHeight);
    setMaxWidthInternal(maxWidth);
  }, [maxHeight, maxWidth]);

  useEffect(() => {
    if (!isView) return undefined;

    const updateViewportSize = () => {
      if (document.fullscreenElement && useFullViewportOnFullscreen) {
        setMaxHeightInternal(document.documentElement.clientHeight);
        setMaxWidthInternal(document.documentElement.clientWidth);
      } else {
        setMaxHeightInternal(maxHeight);
        setMaxWidthInternal(maxWidth);
      }
    };

    const updateAfterFullscreenChange = () => {
      requestAnimationFrame(updateViewportSize);
    };

    updateViewportSize();
    document.addEventListener('fullscreenchange', updateAfterFullscreenChange);
    window.addEventListener('resize', updateViewportSize);

    return () => {
      document.removeEventListener('fullscreenchange', updateAfterFullscreenChange);
      window.removeEventListener('resize', updateViewportSize);
    };
  }, [isView, maxHeight, maxWidth, useFullViewportOnFullscreen]);

  useEffect(() => {
    calcSize();
  }, [enterprise, plant, maxHeightInternal, maxWidthInternal]);

  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [zoom, width, height, maxWidthInternal, maxHeightInternal]);

  useEffect(() => {
    if (!enableTouchpadNavigation || !scrollRef.current) return undefined;
    const element = scrollRef.current;
    const handleWheel = event => onZoom(event);
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [enableTouchpadNavigation, zoom, width, height, maxWidthInternal, maxHeightInternal]);

  const getTouchpadPosition = () => {
    const dimensions = getTouchpadDimensions(zoom);
    const scaledWidth = dimensions.width;
    const scaledHeight = dimensions.height;
    const centeredX = Math.max(0, (maxWidthInternal - scaledWidth) / 2);
    const centeredY = 0;
    const maxPanX = Math.max(0, scaledWidth - maxWidthInternal);
    const maxPanY = Math.max(0, scaledHeight - maxHeightInternal);

    return {
      left: centeredX + Math.min(0, Math.max(-maxPanX, pan.x)),
      top: centeredY + Math.min(0, Math.max(-maxPanY, pan.y)),
    };
  };

  const touchpadPosition = getTouchpadPosition();
  const touchpadDimensions = getTouchpadDimensions(zoom);
  const controlButtonStyle = viewerControls
    ? {
        width: '44px',
        height: '44px',
        padding: 0,
        margin: '0 6px 0 0',
        color: COLORS.WHITE,
        background: 'rgba(36, 39, 43, 0.94)',
        border: '1px solid rgba(255, 255, 255, 0.22)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.28)',
      }
    : {
        margin: '0 8px 0 0',
        background: COLORS.WHITE_LIGHT,
        border: '1px solid ' + COLORS.BLACK_TRANSPARENT,
        zIndex: 3,
      };

  return (
    <Spacing
      display={'block'}
      height={maxHeightInternal + 'px'}
      bg={background}
      sx="margin: 0; padding: 0; overflow: hidden;"
    >
      <Spacing
        display={'block'}
        draggable="false"
        id={'plantScroll'}
        ref={scrollRef}
        onWheel={enableTouchpadNavigation ? undefined : event => onZoom(event)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseEnd}
        onMouseLeave={onMouseEnd}
        onKeyDown={onKeyDown}
        tabIndex={enableTouchpadNavigation ? 0 : undefined}
        aria-label={
          enableTouchpadNavigation ? 'Visualizador da planta. Use mouse, teclado ou touchpad para navegar.' : undefined
        }
        overflow={scrollable}
        height={maxHeightInternal + 'px'}
        width={'100%'}
        sx={
          enableTouchpadNavigation
            ? 'margin: 0; padding: 0; position: relative; touch-action: none;'
            : 'margin: 0; padding: 0; position: relative;'
        }
      >
        {enableTouchpadNavigation ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              margin: 0,
              transform: `translate(${touchpadPosition.left}px, ${touchpadPosition.top}px)`,
              width: touchpadDimensions.width,
              height: touchpadDimensions.height,
              display: 'block',
            }}
          >
            <ImageMapper
              src={
                plant ||
                'https://rockcontent.com/br/wp-content/uploads/sites/2/2019/02/Como-usar-o-Photoshop-1024x538.png'
              }
              height={touchpadDimensions.height}
              imgHeight={enterprise?.imageOrigen?.yScreen}
              width={touchpadDimensions.width}
              imgWidth={enterprise?.imageOrigen?.xScreen}
              map={enterprise?.cords}
              showStatus={false}
              onClick={event => onClick(event)}
            />
          </div>
        ) : (
          <Spacing ai={width * zoom <= maxWidthInternal ? 'center' : 'flex-start'} sx="margin: 0; padding: 0;">
            <ImageMapper
              src={
                plant ||
                'https://rockcontent.com/br/wp-content/uploads/sites/2/2019/02/Como-usar-o-Photoshop-1024x538.png'
              }
              height={height * zoom}
              imgHeight={enterprise?.imageOrigen?.yScreen}
              width={width * zoom}
              imgWidth={enterprise?.imageOrigen?.xScreen}
              map={enterprise?.cords}
              showStatus={false}
              onClick={event => onClick(event)}
            />
          </Spacing>
        )}
      </Spacing>
      <Spacing
        margin={'-60px 0 0 0'}
        fd={'row'}
        jc={'flex-end '}
        sx={viewerControls ? 'padding: 0 16px 16px 0; gap: 6px;' : ''}
      >
        {showBackButton && (
          <IconButton
            style={controlButtonStyle}
            title={'Voltar'}
            onClick={async () => {
              document.exitFullscreen();
              navigate('../admin/empreendimentos');
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {showZoomButtons && (
          <>
            <IconButton style={controlButtonStyle} title={'Zoom In'} onClick={() => setZoom(zoom + 0.25)}>
              <Add />
            </IconButton>
            <IconButton
              style={controlButtonStyle}
              title={'Zoom Out'}
              onClick={() => {
                if (zoom > 1) setZoom(zoom - 0.25);
              }}
            >
              <HorizontalRuleIcon />
            </IconButton>
          </>
        )}
        {showHelpButton && onHelp && (
          <IconButton style={viewerControls ? controlButtonStyle : undefined} title={'Como navegar'} onClick={onHelp}>
            <InfoOutlinedIcon />
          </IconButton>
        )}
        <IconButton
          style={
            viewerControls
              ? controlButtonStyle
              : {
                  margin: isView ? '0 8px 0 0' : '0 16px 0 0',
                  background: COLORS.WHITE_LIGHT,
                  border: '1px solid ' + COLORS.BLACK_TRANSPARENT,
                  zIndex: 3,
                }
          }
          title={'Remover Zoom'}
          onClick={() => setZoom(1)}
        >
          <ZoomInMapIcon />
        </IconButton>
        {showFullscreen && (
          <IconButton
            style={
              viewerControls
                ? controlButtonStyle
                : {
                    margin: '0 16px 0 0',
                    background: COLORS.WHITE_LIGHT,
                    border: '1px solid ' + COLORS.BLACK_TRANSPARENT,
                    zIndex: 3,
                  }
            }
            title={document.fullscreenElement ? 'Sair tela cheia' : 'Colocar em tela cheia'}
            onClick={async () => {
              if (document.fullscreenElement) {
                await document.exitFullscreen();
              } else {
                await document.documentElement.requestFullscreen();
              }
            }}
          >
            <AspectRatioIcon />
          </IconButton>
        )}
      </Spacing>
    </Spacing>
  );
};

export default ImagePlant;
